import React, { useState, useEffect, useRef } from 'react';
import {
  getSyncStores,
  createSyncStore,
  updateSyncStore,
  deleteSyncStore,
  previewSync,
} from '../api';

const STORE_TYPE_LABELS = { aws: '☁️ AWS S3 + EC2' };

function formatDate(iso) {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return d.toLocaleString();
}

function StoreForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(
    initial || {
      name: 'AWS Production',
      store_type: 'aws',
      api_url: 'https://bbmusicmix.com',
      api_token: '',
      s3_bucket: 'bbmusicmix-media',
      s3_region: 'us-west-2',
      s3_prefix: 'music/',
      is_active: true,
    }
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div style={styles.formCard}>
      <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>
        {initial ? 'Edit Remote Store' : 'Add Remote Store'}
      </h3>
      <div style={styles.formGrid}>
        <label style={styles.label}>Name</label>
        <input style={styles.input} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. AWS Production" />

        <label style={styles.label}>Type</label>
        <select style={styles.input} value={form.store_type} onChange={(e) => set('store_type', e.target.value)}>
          <option value="aws">AWS S3 + EC2</option>
        </select>

        <label style={styles.label}>API URL</label>
        <input style={styles.input} value={form.api_url} onChange={(e) => set('api_url', e.target.value)} placeholder="https://bbmusicmix.com" />

        <label style={styles.label}>Admin API Token</label>
        <input style={styles.input} type="password" value={form.api_token} onChange={(e) => set('api_token', e.target.value)} placeholder="Token from EC2 admin user" />

        <label style={styles.label}>S3 Bucket</label>
        <input style={styles.input} value={form.s3_bucket} onChange={(e) => set('s3_bucket', e.target.value)} />

        <label style={styles.label}>S3 Region</label>
        <input style={styles.input} value={form.s3_region} onChange={(e) => set('s3_region', e.target.value)} />

        <label style={styles.label}>S3 Prefix</label>
        <input style={styles.input} value={form.s3_prefix} onChange={(e) => set('s3_prefix', e.target.value)} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button style={styles.btnPrimary} onClick={() => onSave(form)}>Save</button>
        <button style={styles.btnGhost} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function SyncProgress({ lines, plan, done }) {
  const bottomRef = useRef(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines]);

  const phases = ['delete', 'add', 'update'];
  const phaseLabels = { delete: '🗑 Removing', add: '⬆️ Uploading', update: '✏️ Updating' };

  // Find latest progress per phase
  const phaseProgress = {};
  for (const line of lines) {
    if (line.type === 'progress') {
      phaseProgress[line.data.phase] = line.data;
    }
  }

  return (
    <div style={styles.progressPanel}>
      {plan && (
        <div style={styles.planSummary}>
          <span style={styles.planBadge('add')}>+{plan.add} to add</span>
          <span style={styles.planBadge('update')}>~{plan.update} to update</span>
          <span style={styles.planBadge('delete')}>−{plan.delete} to remove</span>
        </div>
      )}

      {phases.map((phase) => {
        const p = phaseProgress[phase];
        const total = plan?.[phase] || 0;
        if (!total && !p) return null;
        const current = p?.current || 0;
        const pct = total > 0 ? Math.round((current / total) * 100) : 0;
        return (
          <div key={phase} style={styles.phaseRow}>
            <div style={styles.phaseLabel}>{phaseLabels[phase]}</div>
            <div style={styles.barOuter}>
              <div style={styles.barInner(pct, phase)} />
            </div>
            <div style={styles.phaseCount}>{current}/{total}</div>
            {p?.label && <div style={styles.phaseFile}>{p.label}</div>}
          </div>
        );
      })}

      <div style={styles.logBox}>
        {lines.map((line, i) => (
          <div key={i} style={styles.logLine(line.type)}>
            {line.type === 'status' && `ℹ ${line.data}`}
            {line.type === 'error' && `⚠ ${line.data}`}
            {line.type === 'progress' && `  ✓ ${line.data.label}`}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {done && (
        <div style={styles.doneBanner}>
          ✅ Sync complete — added {done.added}, updated {done.updated}, removed {done.deleted}
          {done.errors > 0 && `, ${done.errors} errors`}
        </div>
      )}
    </div>
  );
}

export default function RemoteSync() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editStore, setEditStore] = useState(null);
  const [error, setError] = useState('');

  // Per-store sync state
  const [syncState, setSyncState] = useState({}); // storeId → { running, lines, plan, done, preview }

  const loadStores = async () => {
    try {
      const res = await getSyncStores();
      setStores(res.data.results || res.data);
    } catch (e) {
      setError('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStores(); }, []);

  const handleSave = async (form) => {
    try {
      if (editStore) {
        await updateSyncStore(editStore.id, form);
      } else {
        await createSyncStore(form);
      }
      setShowForm(false);
      setEditStore(null);
      loadStores();
    } catch (e) {
      const msg = e.response?.data ? JSON.stringify(e.response.data) : e.message;
      setError(`Failed to save store: ${msg}`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this remote store?')) return;
    await deleteSyncStore(id);
    loadStores();
  };

  const handlePreview = async (store) => {
    setSyncState((s) => ({ ...s, [store.id]: { ...s[store.id], previewLoading: true, preview: null } }));
    try {
      const res = await previewSync(store.id);
      setSyncState((s) => ({ ...s, [store.id]: { ...s[store.id], previewLoading: false, preview: res.data } }));
    } catch (e) {
      setSyncState((s) => ({ ...s, [store.id]: { ...s[store.id], previewLoading: false, preview: { error: String(e) } } }));
    }
  };

  const handleSync = (store) => {
    setSyncState((s) => ({
      ...s,
      [store.id]: { running: true, lines: [], plan: null, done: null, preview: s[store.id]?.preview },
    }));

    const rawToken = localStorage.getItem('musicapp_token');
    // Use EventSource directly on port 8000 (CRA dev proxy buffers SSE).
    // Pass token via query param since EventSource can't set headers.
    const url = `http://localhost:8000/api/sync/stores/${store.id}/sync/?token=${encodeURIComponent(rawToken)}`;
    const es = new EventSource(url);

    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        setSyncState((s) => {
          const prev = s[store.id] || {};
          const newLines = [...(prev.lines || [])];

          if (msg.type === 'plan') {
            return { ...s, [store.id]: { ...prev, plan: msg.data } };
          }
          if (msg.type === 'done') {
            es.close();
            loadStores();
            return { ...s, [store.id]: { ...prev, done: msg.data, running: false } };
          }
          if (msg.type === 'progress') {
            newLines.push(msg);
            return { ...s, [store.id]: { ...prev, lines: newLines } };
          }
          // status / error
          newLines.push(msg);
          return { ...s, [store.id]: { ...prev, lines: newLines } };
        });
      } catch {}
    };

    es.onerror = () => {
      es.close();
      setSyncState((s) => ({
        ...s,
        [store.id]: {
          ...(s[store.id] || {}),
          running: false,
          lines: [...(s[store.id]?.lines || []), { type: 'error', data: 'Connection lost' }],
        },
      }));
    };
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🔄 Remote Sync</h1>
          <p style={styles.subtitle}>
            Push your local library (source of record) to remote data stores.
            Adds new loved tracks, updates changed metadata, and removes unfavorited tracks.
          </p>
        </div>
        <button style={styles.btnPrimary} onClick={() => { setShowForm(true); setEditStore(null); }}>
          + Add Store
        </button>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {(showForm && !editStore) && (
        <StoreForm onSave={handleSave} onCancel={() => setShowForm(false)} />
      )}

      {loading && <div style={styles.emptyState}>Loading stores...</div>}

      {!loading && stores.length === 0 && !showForm && (
        <div style={styles.emptyState}>
          No remote stores configured yet. Add one to start syncing.
        </div>
      )}

      {stores.map((store) => {
        const state = syncState[store.id] || {};
        return (
          <div key={store.id} style={styles.storeCard}>
            {editStore?.id === store.id ? (
              <StoreForm
                initial={editStore}
                onSave={handleSave}
                onCancel={() => setEditStore(null)}
              />
            ) : (
              <>
                <div style={styles.storeHeader}>
                  <div>
                    <div style={styles.storeName}>{store.name}</div>
                    <div style={styles.storeMeta}>
                      {STORE_TYPE_LABELS[store.store_type]} &nbsp;·&nbsp;
                      {store.api_url} &nbsp;·&nbsp;
                      Last synced: <strong>{formatDate(store.last_synced_at)}</strong>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                      style={styles.btnGhost}
                      onClick={() => handlePreview(store)}
                      disabled={state.previewLoading}
                    >
                      {state.previewLoading ? 'Checking...' : '🔍 Preview'}
                    </button>
                    <button
                      style={styles.btnSync}
                      onClick={() => handleSync(store)}
                      disabled={state.running}
                    >
                      {state.running ? '⏳ Syncing...' : '⚡ Sync Now'}
                    </button>
                    <button style={styles.btnGhost} onClick={() => setEditStore(store)}>Edit</button>
                    <button style={styles.btnDanger} onClick={() => handleDelete(store.id)}>Delete</button>
                  </div>
                </div>

                {state.preview && !state.preview.error && (
                  <div style={styles.previewRow}>
                    <span style={styles.planBadge('add')}>+{state.preview.to_add} to add</span>
                    <span style={styles.planBadge('update')}>~{state.preview.to_update} to update</span>
                    <span style={styles.planBadge('delete')}>−{state.preview.to_delete} to remove</span>
                    <span style={styles.previewMeta}>
                      {state.preview.local_loved.toLocaleString()} local loved &nbsp;·&nbsp;
                      {state.preview.remote_total.toLocaleString()} on remote
                    </span>
                  </div>
                )}
                {state.preview?.error && (
                  <div style={{ color: '#f87171', fontSize: 12, marginTop: 8 }}>
                    Preview failed: {state.preview.error}
                  </div>
                )}

                {(state.running || state.done || (state.lines && state.lines.length > 0)) && (
                  <SyncProgress lines={state.lines || []} plan={state.plan} done={state.done} />
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  page: {
    padding: '24px 32px',
    maxWidth: 900,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--text)',
  },
  subtitle: {
    margin: '6px 0 0',
    fontSize: 13,
    color: 'var(--text-muted)',
    maxWidth: 580,
    lineHeight: 1.5,
  },
  errorBanner: {
    background: '#7f1d1d',
    color: '#fca5a5',
    padding: '10px 16px',
    borderRadius: 8,
    fontSize: 13,
    marginBottom: 16,
  },
  emptyState: {
    color: 'var(--text-muted)',
    fontSize: 14,
    padding: '32px 0',
    textAlign: 'center',
  },
  storeCard: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
  },
  storeHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 12,
  },
  storeName: {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--text)',
    marginBottom: 4,
  },
  storeMeta: {
    fontSize: 12,
    color: 'var(--text-muted)',
  },
  formCard: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr',
    gap: '10px 12px',
    alignItems: 'center',
  },
  label: {
    fontSize: 13,
    color: 'var(--text-muted)',
    textAlign: 'right',
  },
  input: {
    background: 'var(--bg)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    color: 'var(--text)',
    fontSize: 13,
    padding: '6px 10px',
    width: '100%',
    boxSizing: 'border-box',
  },
  btnPrimary: {
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  btnSync: {
    background: '#059669',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  btnGhost: {
    background: 'none',
    color: 'var(--text-muted)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '7px 14px',
    fontSize: 13,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  btnDanger: {
    background: 'none',
    color: '#f87171',
    border: '1px solid #7f1d1d',
    borderRadius: 8,
    padding: '7px 14px',
    fontSize: 13,
    cursor: 'pointer',
  },
  previewRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
    flexWrap: 'wrap',
  },
  previewMeta: {
    fontSize: 12,
    color: 'var(--text-muted)',
    marginLeft: 4,
  },
  planBadge: (phase) => ({
    fontSize: 12,
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: 20,
    background: phase === 'add' ? '#14532d' : phase === 'update' ? '#1e3a5f' : '#450a0a',
    color: phase === 'add' ? '#86efac' : phase === 'update' ? '#93c5fd' : '#fca5a5',
  }),
  progressPanel: {
    marginTop: 16,
    borderTop: '1px solid var(--border)',
    paddingTop: 16,
  },
  planSummary: {
    display: 'flex',
    gap: 8,
    marginBottom: 14,
    flexWrap: 'wrap',
  },
  phaseRow: {
    display: 'grid',
    gridTemplateColumns: '120px 1fr 60px auto',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  phaseLabel: {
    fontSize: 12,
    color: 'var(--text-muted)',
    fontWeight: 600,
  },
  barOuter: {
    height: 6,
    background: 'var(--border)',
    borderRadius: 3,
    overflow: 'hidden',
  },
  barInner: (pct, phase) => ({
    height: '100%',
    width: `${pct}%`,
    borderRadius: 3,
    transition: 'width 0.2s ease',
    background: phase === 'add' ? '#22c55e' : phase === 'update' ? '#3b82f6' : '#ef4444',
  }),
  phaseCount: {
    fontSize: 11,
    color: 'var(--text-muted)',
    textAlign: 'right',
  },
  phaseFile: {
    fontSize: 11,
    color: 'var(--text-muted)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 260,
  },
  logBox: {
    marginTop: 12,
    background: 'var(--bg)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '10px 12px',
    maxHeight: 200,
    overflowY: 'auto',
    fontFamily: 'monospace',
    fontSize: 11,
  },
  logLine: (type) => ({
    color: type === 'error' ? '#f87171' : type === 'status' ? '#93c5fd' : 'var(--text-muted)',
    lineHeight: 1.7,
  }),
  doneBanner: {
    marginTop: 12,
    background: '#14532d',
    color: '#86efac',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
  },
};
